import { create } from 'zustand';
import type { Car, CarFilters, SortField, SortOrder, FilterOptions } from '@/types';
import { getCars, getFilterOptions, GetCarsParams } from '@/lib/api';

interface CarState {
  cars: Car[];
  selectedCar: Car | null;
  filters: CarFilters;
  sortBy: SortField;
  sortOrder: SortOrder;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  filterOptions: FilterOptions | null;

  // Actions
  setFilters: (filters: Partial<CarFilters>) => void;
  clearFilters: () => void;
  setSorting: (sortBy: SortField, sortOrder: SortOrder) => void;
  setPage: (page: number) => void;
  setSelectedCar: (car: Car | null) => void;
  fetchCars: () => Promise<void>;
  fetchFilterOptions: () => Promise<void>;
}

const initialFilters: CarFilters = {};

export const useCarStore = create<CarState>((set, get) => ({
  cars: [],
  selectedCar: null,
  filters: initialFilters,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  isLoading: false,
  error: null,
  filterOptions: null,

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      page: 1, // Reset to first page when filters change
    }));
  },

  clearFilters: () => {
    set({ filters: initialFilters, page: 1 });
  },

  setSorting: (sortBy, sortOrder) => {
    set({ sortBy, sortOrder, page: 1 });
  },

  setPage: (page) => {
    set({ page });
  },

  setSelectedCar: (car) => {
    set({ selectedCar: car });
  },

  fetchCars: async () => {
    const { filters, sortBy, sortOrder, page, limit } = get();

    set({ isLoading: true, error: null });

    try {
      const params: GetCarsParams = {
        ...filters,
        sortBy,
        sortOrder,
        page,
        limit,
      };

      const response = await getCars(params);

      set({
        cars: response.data,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch cars',
        isLoading: false,
      });
    }
  },

  fetchFilterOptions: async () => {
    try {
      const response = await getFilterOptions();
      if (response.data) {
        set({ filterOptions: response.data });
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  },
}));
